import React, { useEffect, useState } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { Modal, Button } from 'react-bootstrap';

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [totalPaidToday, setTotalPaidToday] = useState(0);
  const [historyCustomerName, setHistoryCustomerName] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyInvoiceNo, setHistoryInvoiceNo] = useState('');

  const userId = localStorage.getItem('userId');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchInvoices();
    fetchTodayPayments();
  }, [userId]);

  const fetchInvoices = async () => {
    try {
      const res = await api.get('/invoices', { params: { userId } });
      setInvoices(res.data);
    } catch (err) {
      console.error('Error fetching invoices', err);
    }
  };

  const fetchTodayPayments = async () => {
    try {
      const res = await api.get('/invoices/payments/today');
      setTotalPaidToday(parseFloat(res.data.totalPaidToday) || 0);
    } catch {
      setTotalPaidToday(0);
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Invoice List Report', 14, 14);

    const tableRows = filtered.map(inv => {
      const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
      const paid = Number(inv.PaidAmount || 0);
      const pending = Math.max(total - paid, 0);
      const status = inv.PaymentStatus === 'Partial'
        ? `Partial (₹${paid.toFixed(2)}/₹${total.toFixed(2)}, ₹${pending.toFixed(2)} Due)`
        : inv.PaymentStatus === 'Paid'
        ? `Paid (₹${pending.toFixed(2)} Due)`
        : `Pending (₹${pending.toFixed(2)} Due)`;

      const formattedDate = format(new Date(inv.InvoiceDate), 'dd-MM-yyyy');

      return [
        inv.InvoiceNo,
        formattedDate,
        inv.CustomerName,
        total.toFixed(2),
        status
      ];
    });

    autoTable(doc, {
      head: [['Invoice No', 'Date', 'Customer', 'Total (Rs)', 'Status']],
      body: tableRows,
      margin: { top: 20 }
    });

    doc.text(`Total Paid: ₹${totalPaid.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 10);
    doc.text(`Total Pending: ₹${totalPending.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 16);
    doc.text(`Today's Paid: ₹${totalPaidToday.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 22);

    doc.save('Invoice_List.pdf');
  };

  const handleViewHistory = async (invoiceId, invoiceNo, customerName) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/payments`);
      setPaymentHistory(res.data || []);
      setHistoryInvoiceNo(invoiceNo);
      setHistoryCustomerName(customerName);
      setShowHistory(true);
    } catch (err) {
      console.error('Failed to fetch payment history', err);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await api.delete(`/invoices/${invoiceId}`);
        fetchInvoices();
      } catch (err) {
        console.error('Error deleting invoice', err);
      }
    }
  };

  const filtered = invoices.filter(inv => {
    const matchesSearch =
      (inv.CustomerName || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.InvoiceNo || '').toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === 'All' || inv.PaymentStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const totalPaid = filtered.reduce((sum, inv) => sum + Number(inv.PaidAmount || 0), 0);
  const totalPending = filtered.reduce((sum, inv) => {
    const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
    const paid = Number(inv.PaidAmount || 0);
    return sum + Math.max(total - paid, 0);
  }, 0);

  return (
    <div className="container mt-4">
    
      <h4 className="mb-3">All Invoices</h4>  

      <div className="row mb-3 g-2">
        <div className="col-md-3">
          <div className="card text-white bg-success shadow-sm">
            <div className="card-body p-2">
              <h6 className="card-title mb-1">Total Paid</h6>
              <h5 className="card-text">₹{totalPaid.toFixed(2)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-danger shadow-sm">
            <div className="card-body p-2">
              <h6 className="card-title mb-1">Total Pending</h6>
              <h5 className="card-text">₹{totalPending.toFixed(2)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-white bg-primary shadow-sm">
            <div className="card-body p-2">
              <h6 className="card-title mb-1">Today's Paid</h6>
              <h5 className="card-text">₹{totalPaidToday.toFixed(2)}</h5>
            </div>
          </div>
        </div>
        <div className="col-md-3 text-end">
          <button className="btn btn-outline-primary w-100" onClick={handleDownloadPDF}>
            ⬇️ Download PDF
          </button>
          <BackButton />
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Search by customer or invoice number"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Partial">Partial</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped">
          <thead className="table-dark">
            <tr>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Total (₹)</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(inv => {
              const total = Number(inv.TotalAmount || inv.Total || 0) - Number(inv.Discount || 0);
              const paid = Number(inv.PaidAmount || 0);
              const pending = Math.max(total - paid, 0);

              const badgeClass = inv.PaymentStatus === 'Paid'
                ? 'bg-success'
                : inv.PaymentStatus === 'Partial'
                ? 'bg-warning text-dark'
                : 'bg-danger';

              const badgeText = inv.PaymentStatus === 'Partial'
                ? `Partial (₹${paid.toFixed(2)}/₹${total.toFixed(2)}, ₹${pending.toFixed(2)} Due)`
                : inv.PaymentStatus === 'Paid'
                ? `Paid (₹${pending.toFixed(2)} Due)`
                : `Pending (₹${pending.toFixed(2)} Due)`;

              const hoverText = `Paid: ₹${paid.toFixed(2)} | Pending: ₹${pending.toFixed(2)}`;

              return (
                <tr key={inv.InvoiceID}>
                  <td>{inv.InvoiceNo}</td>
                  <td>{format(new Date(inv.InvoiceDate), 'dd-MM-yyyy')}</td>
                  <td>{inv.CustomerName}</td>
                  <td>{total.toFixed(2)}</td>
                  <td>
                    <span className={`badge ${badgeClass}`} title={hoverText}>
                      {badgeText}
                    </span>
                  </td>
                  <td>
  <button
    className="btn btn-sm btn-primary"
    onClick={() => navigate(`/invoice/view/${inv.InvoiceID}`)}
  >
    View
  </button>

  {inv.PaymentStatus !== 'Paid' && (
    <>
      <button
        className="btn btn-sm btn-warning ms-2"
        onClick={() => navigate(`/invoice/edit/${inv.InvoiceID}`)}
      >
        Edit
      </button>

      <button
        className="btn btn-sm btn-danger ms-2"
        onClick={() => handleDelete(inv.InvoiceID)}
      >
        Delete
      </button>
    </>
  )}

  {pending > 0 && (
    <button
      className="btn btn-sm btn-success ms-2"
      onClick={() => navigate(`/invoice/payment/${inv.InvoiceID}`)}
    >
      Receipts
    </button>
  )}

  <button
    className="btn btn-sm btn-secondary ms-2"
    onClick={() => handleViewHistory(inv.InvoiceID, inv.InvoiceNo, inv.CustomerName)}
  >
    View History
  </button>
</td>

                </tr>
              );
            })}
            {currentItems.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">No invoices found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <small>Page {currentPage} of {totalPages}</small>
        <div>
          <button className="btn btn-sm btn-outline-secondary me-2"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            ◀ Prev
          </button>
          <button className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next ▶
          </button>
        </div>
      </div>

      <Modal show={showHistory} onHide={() => setShowHistory(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Payment History - {historyInvoiceNo} <br />
            <small className="text-muted">{historyCustomerName}</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentHistory.length > 0 ? (
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount (₹)</th>
                  <th>Mode</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((p, idx) => (
                  <tr key={idx}>
                    <td>{format(new Date(p.PaymentDate), 'dd-MM-yyyy')}</td>
                    <td>₹{parseFloat(p.PaidAmount).toFixed(2)}</td>
                    <td>{p.PaymentMode}</td>
                    <td>{p.Remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted">No payments found for this invoice.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
